import { useReactiveVar } from '@apollo/client'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { navigateToExternalLink } from '@deltek/specification-client/core/Utilities/urlUtils'
import {
    Button,
    CircularProgress,
    Divider,
    Typography,
} from '@material-ui/core'
import AppBar from '@material-ui/core/AppBar'
import { AssignmentTurnedInOutlined } from '@material-ui/icons'
import SettingsIcon from '@material-ui/icons/Settings'
import { reactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { reactiveWorkspaceContents } from 'api/graphQL/reactiveVars/workspaceContent'
import { ActionButton } from 'components/ActionBar'
import { ToolTipText } from 'components/ActionBar/ToolTipText'
import {
    useActionBarActions,
    useIsPDFExportLoading,
    useIsSupportingDocsLoading,
    useIsWordExportLoading,
    usePDFExportURL,
    useSupportingDocsUrl,
    useWordExportURL,
} from 'components/WorkspaceEditor/ActionBarStore'
import { useElementItemStore } from 'components/WorkspaceEditor/ElementItemStore'
import { useActionBarHandler } from 'components/WorkspaceEditor/hooks/useActionBarHandler'
import { useWorkspace2ActionBarStyles } from 'components/WorkspaceEditor/styles/useWorkspace2ActionBarStyles'
import {
    useIsTreeLoading,
    useWorkspace2Actions,
} from 'components/WorkspaceEditor/Workspace2Store'
import { ProductFamilyStudioManagement } from 'models/StudioManagement/studioManagement'
import AddRemoveProjectButton from 'pages/ProjectsPage/WorkspacePage/components/AddRemoveProjectButton'
import {
    ActionButtonVariant,
    PROPERTY_EDITOR_SHORTCUT_KEY_MAPPING as ShortcutKeys,
} from 'pages/ProjectsPage/WorkspacePage/components/constants'
import ShowHideToggleButton from 'pages/ProjectsPage/WorkspacePage/components/ShowHideToggleButton'
import { WorkspaceActionBarContext } from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceActionBarContext'
import React, { useEffect, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { getNestedObjectPropertyValue } from 'shared/utils/objectUtils'
import {
    getFromLocalStorage,
    updatePersistentConfigs,
} from 'shared/utils/workspaceUtils'
import { ExpandCollapseButton } from './ExpandCollapseButton'
import ExportPDFIcon from './ExportPDFIcon'
import ExportWordIcon from './ExportWordIcon'
import PreviewIcon from './PreviewIcon'
import SMMActionButtons from './SMMActionButtons'

export interface Workspace2ActionBarItem {
    label?: string
    stateKey: string
}
interface AddRemoveProjectProps {
    updateElementList?: (
        elementList: ProjectElementNode[],
        parentElementNodes?: ProjectElementNode[],
        deletedElementNodes?: ProjectElementNode[]
    ) => void
    isOnlyAddedToProject?: boolean
    selectedElementFormat?: string
    allElementsList?: ProjectElementNode[]
}
export interface Workspace2ActionBarProps {
    selectedProjectNode?: ProjectElementNode | null
    selectedTopic?: Partial<ProductFamilyStudioManagement>
    userId?: string
    projectId?: string
    isLoading: boolean
    onProjectSettingsClicked?: () => void
    contentTeamFromStudioPage?: boolean
    addRemoveProjectProps?: AddRemoveProjectProps
}

interface LocationState {
    from: string
    fromStudio?: boolean
    selectedElementIdFromDesignProgressTable?: string
}

const Workspace2ActionBar = ({
    selectedProjectNode,
    selectedTopic,
    userId,
    projectId = '',
    isLoading,
    onProjectSettingsClicked,
    contentTeamFromStudioPage,
    addRemoveProjectProps,
}: Workspace2ActionBarProps) => {
    const { pathname, state } = useLocation()

    const { currentState, setStateValue } = React.useContext(
        WorkspaceActionBarContext
    )

    const { currentUserActiveFirm } = React.useContext(AppBarContext)
    const { elementItemValue, setElementItemValue } = useElementItemStore(
        (s) => ({
            elementItemValue: s.elementItemValue,
            setElementItemValue: s.setElementItemValue,
        })
    )

    const isExportPDFLoading = useIsPDFExportLoading()
    const isExportWordLoading = useIsWordExportLoading()
    const isSupportingDocumentsLoading = useIsSupportingDocsLoading()
    const pdfExportUrl = usePDFExportURL()
    const wordExportUrl = useWordExportURL()
    const supportingDocsUrl = useSupportingDocsUrl()
    const { setExportUrl, setSupportingDocsUrl } = useActionBarActions()
    const isUserRedirectedFromStudio = React.useMemo(
        () =>
            (state as LocationState)?.fromStudio ||
            pathname.includes('/DraftMasterSpecProject/') ||
            pathname.includes(
                `/${currentUserActiveFirm?.bestPracticeProjectId}/`
            ),
        [state, pathname]
    )

    const classes = useWorkspace2ActionBarStyles({
        isOMorSMM: isUserRedirectedFromStudio,
    })
    const { projectProductFamilyId } = reactiveWorkspaceContents()
    const {
        handleCollapseAll,
        handleExpandAll,
        handleExportPDF,
        handleExportWord,
        handleViewPrintPreview,
        handleViewSupportingDocs,
        keysToExpand,
        expandCollapseDisabled,
    } = useActionBarHandler(projectId, projectProductFamilyId!)

    const handleToggleSwitchValueChange = React.useCallback(
        (stateKey: string) => {
            if (stateKey.includes('betaWorkspace')) {
                const toggledValue = !getFromLocalStorage(
                    userId ?? '',
                    'Global',
                    'betaWorkspaceToggleChecked'
                )

                if (toggledValue) {
                    setElementItemValue({
                        ...elementItemValue,
                        elementId: '',
                        elementType: '',
                        baseElementId: '',
                    })
                }

                updatePersistentConfigs(
                    userId,
                    'Global',
                    'betaWorkspaceToggleChecked',
                    toggledValue
                )

                setStateValue('showHideStates.betaWorkspace', toggledValue)
                return
            }

            setStateValue(
                stateKey,
                !getNestedObjectPropertyValue(stateKey, currentState),
                true
            )
        },
        [currentState, setStateValue]
    )

    const { switchWorkspace } = useWorkspace2Actions()
    const isTreeLoading = useIsTreeLoading()

    useEffect(() => {
        const isWorkspace2 = getFromLocalStorage(
            userId ?? '',
            'Global',
            'betaWorkspaceToggleChecked'
        )
        setStateValue('showHideStates.betaWorkspace', isWorkspace2)
        switchWorkspace(isWorkspace2)
    }, [])

    const checkedState = React.useCallback(
        (
            barItem: Workspace2ActionBarItem,
            currentState: Record<string, any>,
            isInProject: boolean | undefined
        ) => {
            const { stateKey, label } = barItem
            if (
                label &&
                ['Project Properties', 'Track Changes'].includes(label) &&
                !isInProject
            ) {
                return false
            }
            return getNestedObjectPropertyValue(stateKey, currentState)
        },
        []
    )
    const evaluationsDisabled = useMemo(
        () =>
            !(
                selectedProjectNode?.elementType !== 'ASSEMBLY' || selectedTopic
            ) ||
            isLoading ||
            isTreeLoading,
        [selectedProjectNode, selectedTopic, isLoading, isTreeLoading]
    )
    const exportDisabled = useMemo(
        () => evaluationsDisabled || !selectedProjectNode?.isInProject,
        [selectedProjectNode, evaluationsDisabled]
    )
    useEffect(() => {
        if (pdfExportUrl) {
            navigateToExternalLink(pdfExportUrl, true)
            setExportUrl('pdf', '')
        }
        if (wordExportUrl) {
            navigateToExternalLink(wordExportUrl, true)
            setExportUrl('docx', '')
        }
        if (supportingDocsUrl) {
            window?.open(supportingDocsUrl, '_blank')?.focus()
            setSupportingDocsUrl('')
        }
    }, [pdfExportUrl, wordExportUrl, supportingDocsUrl])

    const { showSMMActionButtons } = useReactiveVar(reactiveWorkspaceActions)

    return (
        <AppBar
            elevation={0}
            position="relative"
            className={classes.showHideContainer}
        >
            <GridContainer className={classes.contentContainer}>
                <GridItem xs={2}></GridItem>
                <GridItem xs={8} className={classes.actionContainer}>
                    {!contentTeamFromStudioPage ? (
                        <HoverTooltip
                            text={
                                <ToolTipText
                                    label="Evaluations"
                                    smallLabel={ShortcutKeys.SupportingDocs}
                                />
                            }
                        >
                            <span>
                                <Button
                                    data-testid="evaluationsButton"
                                    onClick={handleViewSupportingDocs}
                                    className={classes.evaluationsButton}
                                    startIcon={
                                        isSupportingDocumentsLoading ? (
                                            <CircularProgress
                                                size={16}
                                                className={classes.loadingIcon}
                                            />
                                        ) : (
                                            <AssignmentTurnedInOutlined />
                                        )
                                    }
                                    disabled={
                                        evaluationsDisabled ||
                                        isSupportingDocumentsLoading
                                    }
                                    aria-label="Evaluations"
                                >
                                    Evaluations
                                </Button>
                            </span>
                        </HoverTooltip>
                    ) : (
                        showSMMActionButtons && <SMMActionButtons />
                    )}
                    <Divider
                        orientation="vertical"
                        className={classes.divider}
                    />
                    <Typography className={classes.exportLabel}>
                        Preview & Export
                    </Typography>
                    <ActionButton
                        onClick={handleViewPrintPreview}
                        disabled={exportDisabled}
                        variant={ActionButtonVariant.Preview}
                        tooltip={{
                            label: 'Preview',
                            smallLabel: ShortcutKeys.Preview,
                        }}
                        aria-label="Preview"
                        iconOverlaySize={26}
                    >
                        <PreviewIcon
                            className={
                                exportDisabled
                                    ? classes.iconButtonDisabled
                                    : classes.iconButton
                            }
                        />
                    </ActionButton>
                    {!contentTeamFromStudioPage && (
                        <ActionButton
                            onClick={handleExportPDF}
                            disabled={exportDisabled || isExportPDFLoading}
                            variant={ActionButtonVariant.Export}
                            tooltip={{
                                label: 'Export to PDF',
                                smallLabel: ShortcutKeys.ExportPDF,
                            }}
                            aria-label="Export to PDF"
                            iconOverlaySize={26}
                        >
                            {isExportPDFLoading ? (
                                <CircularProgress
                                    size={16}
                                    className={classes.loadingIcon}
                                />
                            ) : (
                                <ExportPDFIcon
                                    className={
                                        exportDisabled
                                            ? classes.iconButtonDisabled
                                            : classes.iconButton
                                    }
                                />
                            )}
                        </ActionButton>
                    )}
                    <ActionButton
                        onClick={handleExportWord}
                        disabled={exportDisabled || isExportWordLoading}
                        variant={ActionButtonVariant.Export}
                        tooltip={{
                            label: 'Export to Doc/Word',
                            smallLabel: ShortcutKeys.ExportWord,
                        }}
                        aria-label="Export to Doc/Word"
                        iconOverlaySize={26}
                    >
                        {isExportWordLoading ? (
                            <CircularProgress
                                size={16}
                                className={classes.loadingIcon}
                            />
                        ) : (
                            <ExportWordIcon
                                className={
                                    exportDisabled
                                        ? classes.iconButtonDisabled
                                        : classes.iconButton
                                }
                            />
                        )}
                    </ActionButton>
                    <Divider
                        orientation="vertical"
                        className={classes.divider}
                    />
                    <ExpandCollapseButton
                        disabled={expandCollapseDisabled}
                        onExpandAll={handleExpandAll}
                        onCollapseAll={handleCollapseAll}
                        isExpanded={keysToExpand?.length == 0}
                    />
                    {!contentTeamFromStudioPage && (
                        <>
                            <ActionButton
                                onClick={onProjectSettingsClicked}
                                variant={ActionButtonVariant.Settings}
                                tooltip={{
                                    label: 'Workspace Settings',
                                }}
                                aria-label="Workspace Settings"
                                iconOverlaySize={26}
                            >
                                <SettingsIcon className={classes.iconButton} />
                            </ActionButton>

                            <AddRemoveProjectButton
                                projectId={projectId}
                                projectElementNode={selectedProjectNode!}
                                contentTeamFromStudioPage={
                                    contentTeamFromStudioPage
                                }
                                {...addRemoveProjectProps}
                            />
                        </>
                    )}
                </GridItem>
                <GridItem
                    key={'showHideStates.betaWorkspace'}
                    className={classes.switchContainer}
                    xs={2}
                >
                    <ShowHideToggleButton
                        checked={
                            !checkedState(
                                {
                                    label: 'Switch to Classic Workspace',
                                    stateKey: 'showHideStates.betaWorkspace',
                                },
                                currentState,
                                selectedProjectNode?.isInProject
                            )
                        }
                        label={'Switch to Classic Workspace'}
                        isWorkspaceToggle={true}
                        labelPlacement="start"
                        onChange={() =>
                            handleToggleSwitchValueChange(
                                'showHideStates.betaWorkspace'
                            )
                        }
                    />
                </GridItem>
            </GridContainer>
        </AppBar>
    )
}
export default Workspace2ActionBar
